import { useEffect, useState } from 'react'
import Button from '../components/ui/Button'
import DataTable from '../components/ui/DataTable'
import ErrorState from '../components/ui/ErrorState'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import SectionCard from '../components/ui/SectionCard'
import { apiClient } from '../services/api'
import { useTranslation } from '../contexts/TranslationContext'
import { useAuthStore } from '../services/authStore'
import { fetchI18nSyncStatus, syncI18nWithCode } from '../services/translationService'
import { ui } from '../styles/ui'
import { formatDateTime } from '../utils/date'

function StatsTile({ label, value, mono = false }) {
  return (
    <div className={ui.cards.compact}>
      <div className="text-muted-foreground text-sm">{label}</div>
      <div className={mono ? 'font-mono text-xs break-all' : 'font-semibold'}>{value}</div>
    </div>
  )
}

export default function Languages() {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const isAdmin = Boolean(user?.is_admin ?? user?.isAdmin)
  const [languages, setLanguages] = useState([])
  const [syncStatus, setSyncStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [syncing, setSyncing] = useState(false)

  const fetchLanguages = async () => {
    const response = await apiClient.get('/languages')
    const data = response.data?.data ?? response.data
    setLanguages(Array.isArray(data) ? data : [])
  }

  const fetchStatus = async () => {
    try {
      const status = await fetchI18nSyncStatus()
      setSyncStatus(status)
    } catch (_error) {
      setSyncStatus(null)
    }
  }

  const loadAll = async () => {
    setLoading(true)
    setError('')
    try {
      await Promise.all([fetchLanguages(), fetchStatus()])
    } catch (_error) {
      setError(t('Could not load language status', 'Could not load language status'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAll()
  }, [])

  const handleSync = async () => {
    setSyncing(true)
    setError('')
    try {
      await syncI18nWithCode()
      await loadAll()
    } catch (err) {
      setError(err?.response?.data?.message || t('Sync with code failed', 'Sync with code failed'))
    } finally {
      setSyncing(false)
    }
  }

  const canSync = Boolean(isAdmin && syncStatus?.admin_sync_action_enabled)
  const lastSyncValue = syncStatus?.last_synced_timestamp
    ? formatDateTime(syncStatus.last_synced_timestamp)
    : 'Never'

  const columns = [
    { key: 'code', label: t('translations.code', 'Code'), sortable: true },
    { key: 'name', label: t('translations.name', 'Name'), sortable: true },
    { key: 'native_name', label: t('translations.native_name', 'Native name'), sortable: true },
    { key: 'direction', label: t('translations.direction', 'Direction'), sortable: true },
    { key: 'active', label: t('translations.active', 'Active'), sortable: true, sortAccessor: (lang) => (lang.active ? 1 : 0) },
  ]

  return (
    <PageShell>
      <PageHeader
        title={t('languages.title', 'Language Management')}
        subtitle={t('languages.file_backed_notice', 'Languages are managed by code files (config/i18n/languages.json). Runtime editing is disabled. Run Sync with code after source changes.')}
        actions={(
          <Button onClick={handleSync} disabled={!canSync || syncing}>
            {syncing ? t('languages.syncing', 'Syncing...') : t('languages.sync_with_code', 'Sync with code')}
          </Button>
        )}
      />

      {!canSync ? (
        <div className={ui.forms.helpText}>
          {t('languages.sync_disabled', 'Sync action is disabled or unavailable for this account.')}
        </div>
      ) : null}

      {error ? <ErrorState text={error} /> : null}

      <div className={ui.layout.contentGrid}>
        <StatsTile label={t('languages.source', 'Source')} value={t('Code files', 'Code files')} />
        <StatsTile label={t('languages.default_language', 'Default language')} value={syncStatus?.default_language || 'n/a'} />
        <StatsTile label={t('languages.sync_timestamp', 'Last sync')} value={lastSyncValue} />
        <StatsTile label={t('languages.enabled_count', 'Enabled languages')} value={syncStatus?.enabled_language_count ?? languages.filter((l) => l.active).length} />
        <StatsTile label={t('languages.total_count', 'Total languages')} value={syncStatus?.language_count ?? languages.length} />
        <StatsTile label={t('languages.source_hash', 'Source hash')} value={syncStatus?.source_hash || 'n/a'} mono />
      </div>

      <SectionCard title={t('languages.title', 'Language Management')}>
        {loading ? (
          <LoadingState text={t('Loading languages...', 'Loading languages...')} />
        ) : (
          <DataTable
            columns={columns}
            rows={languages}
            emptyText={t('translations.no_languages', 'No languages configured')}
            renderRow={(lang) => (
              <tr key={lang.code} className={ui.tableRow}>
                <td className={ui.tableCellText}>{lang.code}</td>
                <td className={ui.tableCellText}>{lang.name}</td>
                <td className={ui.tableCellText}>{lang.native_name}</td>
                <td className={ui.tableCellText}>{lang.direction}</td>
                <td className={ui.tableCellText}>{lang.active ? t('Yes', 'Yes') : t('No', 'No')}</td>
              </tr>
            )}
          />
        )}
      </SectionCard>
    </PageShell>
  )
}
